import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingCart, ImageOff } from "lucide-react";
import Rating from "@/components/common/Rating";
import { formatPrice, discountPercent } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n/I18nProvider";

export default function ProductCard({ product }) {
  const { t } = useI18n();
  const { toggleWishlist, inWishlist, addToCart } = useApp();
  const wished = inWishlist(product.id);
  const off = discountPercent(product.regularPrice, product.salePrice);
  const hasVariants = (product.attributes || []).length > 0;
  const inStock = product.inStock !== false;

  return (
    <div className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card hover-lift" data-testid="product-card">
      <Link to={`/product/${product.slug}`} className="relative block aspect-square overflow-hidden bg-secondary">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-muted-foreground"><ImageOff size={28} /></div>
        )}
        {off > 0 && (
          <span className="absolute start-3 top-3 rounded-full bg-brand px-2 py-0.5 text-xs font-bold text-brand-foreground shadow-soft">-{off}%</span>
        )}
        {!inStock && (
          <span className="absolute start-3 bottom-3 rounded-full bg-ink/80 px-2 py-0.5 text-xs font-semibold text-white">{t("out_of_stock")}</span>
        )}
      </Link>

      <button
        onClick={() => toggleWishlist(product)}
        aria-label={t("add_to_wishlist")}
        data-testid={`wishlist-toggle-${product.id}`}
        className="absolute end-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-background/90 text-ink shadow-soft backdrop-blur transition hover:text-brand"
      >
        <Heart size={17} className={wished ? "fill-brand text-brand" : ""} />
      </button>

      <div className="flex flex-1 flex-col p-3">
        <Link to={`/product/${product.slug}`} className="line-clamp-2 min-h-[2.5rem] text-sm font-medium text-ink hover:text-brand">
          {product.name}
        </Link>
        <div className="mt-1.5">
          {product.rating > 0 ? (
            <Rating rating={product.rating} count={product.reviewCount} size={13} />
          ) : (
            <span className="text-xs text-muted-foreground">{t("no_reviews")}</span>
          )}
        </div>
        <div className="mt-auto pt-3">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-lg font-bold text-ink">{formatPrice(product.price, product.currency)}</span>
            {off > 0 && (
              <span className="text-xs text-muted-foreground line-through">{formatPrice(product.regularPrice, product.currency)}</span>
            )}
          </div>
        </div>
      </div>

      <div className="p-3 pt-0">
        {hasVariants ? (
          <Link
            to={`/product/${product.slug}`}
            className="flex h-10 w-full items-center justify-center rounded-full border border-ink text-sm font-semibold text-ink transition hover:bg-ink hover:text-white"
            data-testid={`select-options-${product.id}`}
          >
            {t("select_options")}
          </Link>
        ) : (
          <button
            onClick={() => addToCart(product, 1)}
            disabled={!inStock}
            data-testid={`add-to-cart-${product.id}`}
            className="flex h-10 w-full items-center justify-center gap-2 rounded-full bg-ink text-sm font-semibold text-white transition hover:bg-brand disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ShoppingCart size={16} /> {t("add_to_cart")}
          </button>
        )}
      </div>
    </div>
  );
}
